const DotGroups = require("./DotGroups");

// This file contains batex diagram helper functions for specific BA lessons.
// Most of these are one-off functions that won't be used outside of the
// intended lesson, and were only added to make lesson construction easier in
// BAeditor. That means there might be a lot of e.g. hard-coding, but these
// methods can always be generalized and moved elsewhere if we find a more
// general use.

/**
* Used by the "Difference" lesson in BA1, chapter 5, among others.
* Draws two rows of symbols with the given counts, shapes, and colors.

* @param {number} numTop number of symbols drawn on the top row
* @param {number} numBottom number of symbols drawn on the bottom row
* @param {string} topSymbol symbol used in the top row
* @param {string} bottomSymbol symbol used in the bottom row (this and
*	topSymbol should be something you can pass into the \symbol command)
* @param {string} color color of the symbols
* @param {boolean} [isSolutionImage=false] if true then the first n symbols in
*	each row will be grayed out, where n is the minimum of numTop and numBottom
* @param {number} [x=0]
* @param {number} [y=0] if x and y are set, then the rows will be drawn at
*	(x,y) and the canvas size will be set so that it fits (NOTE: modifying the
*	canvas context within a draw function is generally a bad idea, but this
*	happened due to the method being expanded with more functionality over time
*	and we wanted to keep backwards compatibility...)
* @param {number} [scale=1] determines the size of the symbols
* @param {string} [bottomColor=null] if set, the color of the bottom row
*/
const drawRowsDifference = function(
	ctx,
	numTop,
	numBottom,
	topSymbol,
	bottomSymbol,
	color,
	isSolutionImage = false,
	x = 0,
	y = 0,
	scale = 1,
	bottomColor = null
) {
	const unit = 18 * scale;
	const width = Math.max(numTop, numBottom);
	const height = 2;

	// Edge margins
	const top = 2;
	const bottom = 2;
	const left = 3;
	const right = 3;

	const topSymbolColor = `$\\smaller[1.7]{\\symbol[${color}]{${topSymbol}}}$`;
	const topSymbolGray = `$\\smaller[1.7]{\\symbol[lightgray]{${topSymbol}}}$`;
	const bottomSymbolColor = `$\\smaller[1.7]{\\symbol[${bottomColor ||
		color}]{${bottomSymbol}}}$`;
	const bottomSymbolGray = `$\\smaller[1.7]{\\symbol[lightgray]{${bottomSymbol}}}$`;

	const smaller = Math.min(numTop, numBottom);
	const topStr = isSolutionImage
		? "x".repeat(smaller) + "X".repeat(numTop - smaller)
		: "X".repeat(numTop);
	const botStr = isSolutionImage
		? "y".repeat(smaller) + "Y".repeat(numBottom - smaller)
		: "Y".repeat(numBottom);

	ctx.save();
	ctx.setSize(
		width * unit + left + right + 2 * x,
		height * unit + top + bottom + 2 * y
	);
	DotGroups(ctx, 2 + x, 2 + y, [topStr, botStr], {
		cellSize: 10,
		symbolMap: {
			X: topSymbolColor,
			x: topSymbolGray,
			Y: bottomSymbolColor,
			y: bottomSymbolGray,
		},
		scale: 1.5 * scale,
	});
	ctx.restore();
};

/**
 * Used by the "On a Roll" lesson in BA1, chapter 9.
 * Given a function that draws a shape, this method draws that shape a given
 * number of times in a row, each rotated by a multiple of a given angle.
 * The shape is rotated around the point (width/2, height/2) and offset by an
 * optional margin.
 *
 * @param {function} drawFunc function that takes no input and draws the shape
 * 	that should be rotated
 * @param {number} width width of thet shape drawn by drawFunc
 * @param {number} height height of the shape drawn by drawFunc
 * @param {number} numRotations number of rotated copies of the shape to draw
 * @param {number} angle angle, in degrees, to rotate each subsequent shape
 * @param {Object} settings additional optional settings
 * @param {number} [settings.x=0]
 * @param {number} [settings.y=0] if x and y are set, then the diagram will be
 *	drawn at (x, y)
 * @param {number} [settings.margin=0] number of pixels to adjust spacing
 *	between shapes
 * @param {number} [settings.pickRotation] if set, this method will only draw the
 *	`pickRotation`-th shape in the row (e.g. if pickRotation=1, it only draws the
 *	original shape). numRotations and margin arguments are ignored in this case.
 */
const drawMultipleRotations = function(
	ctx,
	drawFunc,
	width,
	height,
	numRotations,
	angle,
	settings = {}
) {
	const startX = settings.x || 0;
	const startY = settings.y || 0;
	const margin = settings.margin || 0;

	const drawRotatedShape = function(x, y, degrees) {
		ctx.save();
		// Move to the position and rotate before drawing. This specifically
		// rotates the shape around the point (width/2, height/2).
		ctx.translate(x + width / 2, y + height / 2);
		ctx.rotate((degrees * Math.PI) / 180);
		ctx.translate(-width / 2, -height / 2);
		drawFunc();
		ctx.restore();
	};

	ctx.save();
	if (settings.pickRotation) {
		drawRotatedShape(startX, startY, (settings.pickRotation - 1) * angle);
	} else {
		for (let i = 0; i < numRotations; i++) {
			drawRotatedShape(startX + i * (width + margin), startY, i * angle);
		}
	}
	ctx.restore();
};

module.exports = {
	drawRowsDifference,
	drawMultipleRotations,
};
